import {
	AnnualLoanPaymentDetails,
	ComprehensiveForm,
	ComprehensiveLoanDetails,
	ModalFormType,
	SingleExtraRepayment,
} from "@/types/global";
import { UseFormReturn } from "react-hook-form";

/**
 *
 * @param form
 * @param additionalRepayments
 * @param offset
 * @param singleExtraRepayment
 * @returns Loan Details
 */
export function comprehensiveCalculator(
	form: UseFormReturn<ModalFormType<ComprehensiveForm>, any>,
	singleExtraRepayment: SingleExtraRepayment
): ComprehensiveLoanDetails {
	// Constants
	const MONTHS_PER_YEAR = 12;

	// Initialize Variables
	const { getValues } = form;
	let loanAmount = +getValues("loanAmount")
		.replace(/[^0-9]/g, "")
		.replace(/,/g, "");
	const { interestRate, interestType, repaymentType } = getValues();
	let loanTerm = getValues("loanTerm");
	const { fixedTerm, fixedRate, ongoing } = interestRate;

	const annualLoanDetails: AnnualLoanPaymentDetails[] = [];
	const comprehensiveLoanDetails: ComprehensiveLoanDetails = {
		loanAmount,
		fixedRepayments: 0,
		variableRepayments: 0,
		yearsPassed: 0,
		monthsPassed: 0,
		annualLoanDetails: annualLoanDetails,
	};

	let extraRepayments = 0;

	let interestLoanAmount = loanAmount; // Amount to calculate the interest

	// Reset interestLoanAmount for Variable Rate Period
	interestLoanAmount = loanAmount;

	// Calculate the Variable Repayment
	let monthlyInterestRate = ongoing / 100 / MONTHS_PER_YEAR;

	// Initial repayment calculation based on adjusted loan amount
	const calculateVariableRepayment = (remainingLoan: number, remainingTerm: number) => {
		const denominator = 1 - Math.pow(1 + monthlyInterestRate, -remainingTerm * MONTHS_PER_YEAR);
		const monthlyRepayment =
			(monthlyInterestRate * remainingLoan) / denominator + extraRepayments;

		return +monthlyRepayment.toFixed(2);
	};

	// Balloon payment adjustment if applicable
	if (singleExtraRepayment.active) {
		extraRepayments = +singleExtraRepayment.repaymentAmount * monthlyInterestRate;
		const tempLoanAmount = loanAmount - singleExtraRepayment.repaymentAmount;
		// Recalculate variable repayments based on the new loan amount and remaining term
		comprehensiveLoanDetails.variableRepayments = calculateVariableRepayment(
			tempLoanAmount,
			loanTerm
		);
	} else {
		comprehensiveLoanDetails.variableRepayments = calculateVariableRepayment(
			loanAmount,
			loanTerm
		);
	}

	let totalPrincipalPaid = 0;
	let totalInterestPaid = 0;
	let loanBalance = loanAmount;

	for (let i = 0; i < loanTerm * MONTHS_PER_YEAR; i++) {
		let interestForMonth = loanBalance * monthlyInterestRate;

		let principalPayment = comprehensiveLoanDetails.variableRepayments - interestForMonth;

		// Update loan amount after principal payment
		loanBalance -= principalPayment;

		totalPrincipalPaid += principalPayment;
		totalInterestPaid += interestForMonth;

		// End of year summary
		if ((i + 1) % 12 === 0) {
			comprehensiveLoanDetails.yearsPassed++;

			// Balloon Year Payment
			if (singleExtraRepayment.yearOfPayment === comprehensiveLoanDetails.yearsPassed) {
				// Add the balloon payment to the year specified.
				totalPrincipalPaid += singleExtraRepayment.repaymentAmount;
			}

			annualLoanDetails.push({
				year: comprehensiveLoanDetails.yearsPassed,
				interestPaid: totalInterestPaid,
				principalPaid: totalPrincipalPaid,
			});
			totalInterestPaid = 0;
			totalPrincipalPaid = 0; // Reset for next year
		}

		comprehensiveLoanDetails.monthsPassed++;

		// Break if loan is finished
		if (loanAmount <= 0) {
			loanAmount = 0;
			break;
		}
	}
	return comprehensiveLoanDetails;
}
